import Button from "@/components/buttons/Button";
import ButtonNew from "@/components/buttons/ButtonNew";
import CheckoutLayout from "@/components/checkoutLayout";
import TextInputNew from "@/components/inputs/TextInputNew";
import { getLatestRouteBeforeLogin } from "@/components/layout";
import SignupComponent from "@/components/login/SignupComponent";
import Spinner from "@/components/misc/Spinner";
import { User } from "@/openapi";
import { IStore } from "@/redux/store";
import { stripeApi } from "@/utils/NetworkUtils";
import { setUserGroup, trackEvent } from "@/utils/analyticsFunctions";
import { getAppleClientId, getAppleLoginRedirectURI } from "@/utils/appleLogin";
import {
  captureException,
  devPrint,
  getErrorFromResponse,
  isLocalhost,
} from "@/utils/devUtils";
import {
  IStripeProductPrice,
  durationToReadable,
  getPaymentReturnCancelUrl,
  getPaymentReturnSuccessUrl,
  getProductTitle,
  getStripePublishableKey,
  priceToReadable,
} from "@/utils/paymentUtils";
import { getLocalUser } from "@/utils/sharedPrefrences";
import AppleIcon from "@mui/icons-material/Apple";
import Google from "@mui/icons-material/Google";
import { useGoogleLogin } from "@react-oauth/google";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import AppleLogin from "react-apple-login";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useAuth } from "../auth";
import { _ } from "./_app";
import Checkout from "./checkout";

interface EmailLoginProps {
  onBack: () => any;
  onLoggedIn: () => void;
}

const EmailLogin = (props: EmailLoginProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPass, setForgotPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { loading, isAuthenticated, login } = useAuth();
  const router = useRouter();

  const handleLogin = async () => {
    setIsLoading(true);
    trackEvent("login", { type: "email" });
    try {
      var res: Response = await login(email, password);
      if (res.status > 299) {
        var msg = await getErrorFromResponse(res);
        toast.error("Error logging in\n" + msg);
        setIsLoading(false);
        return;
      }
      setUserGroup("auth", "email");
      props.onLoggedIn();
    } catch (e) {
      console.log("error logging in", e);
    }
    setIsLoading(false);
  };

  return (
    <form
      className=" pt-2 pb-2 mb-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (loading) {
          return;
        }
        handleLogin();
      }}
    >
      <div className="flex flex-col gap-[2.2rem]">
        <div className="flex flex-col gap-[1rem]">
          <TextInputNew
            placeholder="Email"
            onChange={(val) => {
              setEmail(val);
            }}
          />
          <TextInputNew
            type="password"
            placeholder="Password"
            onChange={(val) => {
              setPassword(val);
            }}
          />
        </div>
        <input type="submit" hidden />
        <div className="f flex items-center gap-[1rem]">
          {isLoading ? (
            <div className="flex justify-center">
              <Spinner />
            </div>
          ) : (
            <ButtonNew
              variant="login"
              onClick={() => {
                handleLogin();
              }}
            >
              {_("Login")}
            </ButtonNew>
          )}
          <div
            className={
              "cursor-pointer justify-start flex " +
              (forgotPass ? "" : "underline")
            }
            onClick={() => {
              setForgotPass(true);
            }}
          >
            {forgotPass
              ? _("You can reset your password inside the app")
              : _("Forgot password?")}
          </div>
        </div>
      </div>
    </form>
  );
};

interface ILogin {
  product: IStripeProductPrice | undefined;
  user: User | undefined;
}

const Login = ({ product, ...props }: ILogin) => {
  const [showEmail, setShowEmail] = useState(false);
  const router = useRouter();
  const [showApple, setShowApple] = useState(false);

  const [showSignup, setShowSignup] = useState(false);

  const {
    loading,
    isAuthenticated,

    loginWithGoogle,
    loginWithApple,
  } = useAuth();

  const stripe = useStripe();

  useEffect(() => {
    if (!loading && isAuthenticated && !product) {
      router.replace("/checkout");
    }
  }, [isAuthenticated, loading, product]);

  useEffect(() => {
    // not sure if this is needed
    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.onload = () => {
      setShowApple(true);
    };
    document.body.appendChild(script);

    // weird bug where the script isn't loaded
    setTimeout(() => {
      setShowApple(true);
    }, 1000);
  }, []);

  const gLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      trackEvent("login", { type: "google" });
      loginWithGoogle(tokenResponse.access_token, tokenResponse.access_token)
        .then(async (res: Response) => {
          if (res.status > 299) {
            trackEvent("login_error", { type: "google" });
            var msg = await getErrorFromResponse(res);
            toast.error(msg);
            return;
          }
          setUserGroup("auth", "google");
          handleNavAfterLogin();
        })
        .catch((value: any) => {
          devPrint("EEEEE", value);
          trackEvent("login_error", { type: "google" });
          captureException(value, true);
          toast.error("Error logging in via google: " + value);
        });
    },
  });

  const renderAppleLoginBtn = () => {
    var btnText = showSignup ? _("Sign up with Apple") : _("Login with Apple");

    return (
      <AppleLogin
        usePopup={showApple}
        clientId={getAppleClientId()}
        redirectURI={getAppleLoginRedirectURI()}
        callback={(e) => {
          const code = e.authorization.code;
          const idToken = e.authorization.id_token;
          trackEvent("login", { type: "apple" });
          loginWithApple(code, idToken)
            .then(async (res: Response) => {
              if (res.status > 299) {
                trackEvent("login_error", { type: "google" });
                var msg = await getErrorFromResponse(res);
                toast.error(msg);
                return;
              }
              setUserGroup("auth", "apple");
              handleNavAfterLogin();
            })
            .catch((value: any) => {
              captureException(value, true);
              trackEvent("login_error", { type: "apple" });
              toast.error("Error signing in with Apple " + value);
            });
        }}
        render={(_props) => {
          return (
            <Button
              icon={<AppleIcon className="text-primary-brown" />}
              className="flex gap-[0.5rem] justify-center items-center w-auto whitespace-nowrap rounded-[0.8rem] text-[1.1rem] bg-[#F5E9DC] text-[#6E4A24] font-normal shadow-none"
              onClick={() => {
                if (isLocalhost()) {
                  toast("Apple login does not work on localhost");
                  return;
                }
                _props.onClick();
              }}
            >
              <span className="text-black"> {btnText}</span>
            </Button>
          );
        }}
      />
    );
  };

  const handleNavAfterLogin = async () => {
    var lUser = getLocalUser();

    if (product && stripe && lUser) {
      try {
        const res = await stripeApi.stripeCheckoutSessionCreate({
          cancelUrl: getPaymentReturnCancelUrl(product),
          successUrl: getPaymentReturnSuccessUrl(product),
          priceId: product.price.id,
          coupon: "",
          clientReferenceId: lUser?.id.toString(),
        });

        // When the customer clicks on the button, redirect
        // them to Checkout.
        stripe!
          .redirectToCheckout({
            sessionId: res.id,
          })
          .then(function (result) {
            if (result.error) {
              // If `redirectToCheckout` fails due to a browser or network
              // error, display the localized error message to your customer.
              // setErrorMessage(result.error.message);
              toast.error(result.error.message);
            }
          });
        return;
      } catch (e) {
        captureException(e, true);
      }
    }
    const r = getLatestRouteBeforeLogin();
    if (r) {
      router.push(r);
      return;
    }
    router.push("/");
  };

  const renderButtons = () => {
    return (
      <div
        key={Math.random()}
        className="h-full flex flex-col justify-center text-primary-brown"
      >
        <div className="flex flex-col gap-[1rem] mb-8">
          <div className="font-semibold text-[2.4rem] sm:text-[2.8rem] lg:text-[3.6rem] leading-[2.8rem] sm:leading-[3.2rem] lg:leading-[4rem]">
            {showSignup
              ? _("signup-headline")
              : _("login-headline")
            }
          </div>
          <div>{_("Please enter your details")} </div>
        </div>
        <div className="flex flex-wrap gap-y-[0rem] gap-[1rem]">
          <Button
            icon={<Google className="text-black" />}
            className="flex gap-[0.5rem] justify-center items-center text-[1.1rem] rounded-[0.8rem] w-auto whitespace-nowrap bg-[#F5E9DC] text-[#6E4A24] font-normal shadow-none"
            onClick={() => {
              gLogin();
            }}
          >
            <span className="text-primary-brown">
              {showSignup ? _("Sign up with Google") : _("Login with Google")}
            </span>
          </Button>
          {renderAppleLoginBtn()}
        </div>
        {
          showSignup ? (
            <SignupComponent
              onSignedUp={() => {
                handleNavAfterLogin();
              }}
            />
          ) : (
            <EmailLogin
              onLoggedIn={() => {
                handleNavAfterLogin();
              }}
              onBack={() => {
                setShowEmail(false);
              }}
            />
          )
        }
        <div
          onClick={() => {
            router.push("/login#signup");
            setShowSignup(!showSignup);
          }}
          className="flex items-start gap-[0.25rem] text-xs mb-6 text-stone-gray cursor-pointer text-center"
        >
          {showSignup ? (
            <div>
              {_("Already have an account?")}{" "}
              <span className="underline">{_("Login")}</span>
            </div>
          ) : (
            <>
              {_("If you do not have an account, you can")}{" "}
              <span className="underline">{_("create one!")}</span>
            </>
          )}
        </div>
        <div className="flex items-start gap-[0.25rem] text-xs mb-6 text-stone-gray cursor-pointer text-center">
          <p className="text-left">{_("login-page__terms-of-use-1")}{" "}
            <a
              href="https://www.horseday.com/terms-privacy"
              className="inline text-left underline"
            >
              {_("login-page__terms-of-use-2")}
            </a>
          </p>
        </div>
      </div >
    );
  };

  const renderSelectedProduct = () => {
    if (!product) {
      return null;
    }

    // Check if it's Premium + Annual
    const isPremiumAnnual = product.product.metadata.entitlement === 'PRO' &&
      product.product.metadata.duration === 'YEAR';

    return (
      <div className="bg-[#F5E6DB] p-8 sm:p-10 md:p-12 rounded-[1rem] w-full relative">
        <div className="font-semibold" style={{ fontSize: 24 }}>
          {_("Checkout")}
        </div>
        <div>{_("Please login to continue with your checkout")}</div>

        <div className="mt-3 bg-white p-[16px] md:p-[32px] rounded-[0.75rem]">
          <div className="flex flex-row justify-between">
            <span>{getProductTitle(product!)}</span>
            <span>
              {priceToReadable(
                product.price.monthPrice,
                product.price.currency
              )}{" "}
              / <span className="font-thin">{durationToReadable("MONTH")}</span>
            </span>
          </div>
          {product.product.metadata.duration === "YEAR" && (
            <>
              <hr className="mt-[20px] mb-[20px]" />
              <div className="flex flex-row justify-between">
                <span className="font-semibold">{_("Total after trial")}</span>
                <span>
                  {product.price.discount && (
                    <span className="mr-2 text-stone-gray">
                      {_("You save")}{" "}
                      {(product.price.discount * 100).toFixed(0)} %
                    </span>
                  )}
                  <span>
                    {priceToReadable(
                      product.price.unitAmount,
                      product.price.currency
                    )}{" "}
                    /{" "}
                    <span className="font-thin">
                      {durationToReadable(product.product.metadata.duration)}
                    </span>
                  </span>
                </span>
              </div>
            </>
          )}
        </div>

        {/* ONLY SHOW FOR PREMIUM ANNUAL */}
        {isPremiumAnnual && (
          <div className="flex flex-row gap-[0.8rem] mt-[1rem] justify-start items-center text-[#BF8B56] rounded-[200px] text-[0.9rem] leading-[1.2rem]">
            <p className="">{_("discount-message")}</p>
          </div>
        )}
      </div>
    );
  };

  const renderNotLoggedIn = () => {
    return (
      <div className="relative z-[100] bg-[linear-gradient(180deg,#FFFFFF_0%,#F9F4EF_100%)] flex justify-center items-start pt-[1rem] pb-[5.2rem] top-0 w-full">
        <div
          className="flex flex-col lg:flex-row gap-[4.8rem] max-w-[1360px] px-5 w-full lg:px-10 py-[2.5rem]"
          style={{
            // maxWidth: 1800,
            minHeight: "100vh",
          }}
        >
          <div className="w-full">{renderButtons()}</div>
          {!product ? (
            <div className="hidden 2xl:block xl:block lg:block md:block sm:hidden  ">
              <Image
                src="/loginphoto4.jpg"
                width={1440}
                height={2048}
                alt="image of horse"
                style={{
                  objectFit: "cover",
                  objectPosition: "-27rem",
                  maxWidth: "auto",
                  borderRadius: "2.5rem",
                  height: window.innerHeight - 120,
                }}
              />
            </div>
          ) : (
            renderSelectedProduct()
          )}
        </div>
      </div>
    );
  };

  const renderLoggedIn = () => {
    return <Checkout />;
  };

  return (
    <CheckoutLayout>
      {isAuthenticated ? renderLoggedIn() : renderNotLoggedIn()}
    </CheckoutLayout>
  );
};

const LoginPageContainer = (props: ILogin) => {
  return (
    <Elements stripe={stripePromise}>
      <Login user={props.user} product={props.product} />
    </Elements>
  );
};

const stripePromise = loadStripe(getStripePublishableKey());

const mapStateToProps = (state: IStore) => {
  const data = state.paymentState;
  const { user } = state.userState;

  const { product } = data;
  return { product, user };
};

export default connect(mapStateToProps)(LoginPageContainer);
